
import Matchup from "@/shared/matchups/matchup";
import {MatchupResult} from "@/shared/matchups/matchup";
import orderBy from "lodash/orderBy";
import Owner from "@/shared/owners/owner";
import OwnerTeam from "@/shared/teams/owner-team";
import { ref } from "vue";
import Season from "@/shared/seasons/season";
import Team from "@/shared/teams/team";
import OwnerCard from "../components/OwnerCard.vue";
import { useRouter } from "vue-router";
import { useStore } from 'vuex';
import { watch } from 'vue';

function consolidateSeasons(seasons: Season[]) {
  const teams: Team[] = [];
  const ownerTeams: OwnerTeam[] = [];
  const date = new Date();
  const currentYear: number =
    date.getMonth() > 2 ? date.getFullYear() : date.getFullYear() - 1;
  let memberCount = 1;

// todo this could be shared in a class/store/service since this exists on multiple pages. 
  function getOpponentOwnerTeam(
    ownerTeams: OwnerTeam[],
    opponentId: number,
    season: number
  ): OwnerTeam {
    const baseOwnerTeam: OwnerTeam = {
      id: 0,
      owner: {
        id: "0",
        firstName: "BYE",
        lastName: "WEEK",
      },
    };

    if (opponentId === 0) {
      return baseOwnerTeam;
    }

    for (let i = 0; i < ownerTeams.length; i++) {
      const ownerTeam = ownerTeams[i];
      const teamYear = ownerTeam.teamYears && ownerTeam.teamYears.find((teamYear) => {
        return teamYear.year === season && teamYear.team === opponentId;
      });

      if (teamYear) return ownerTeam;
    }
    
    return baseOwnerTeam;
  }

  function getMatchupScores(
    matchup: Matchup,
    teamSide: string,
    season: Season
  ): MatchupResult[] {
    const matchupScores: MatchupResult[] = [];
    for (const week in matchup[teamSide].pointsByScoringPeriod) {
      let opponentSide = "home";
      if (teamSide === "home") opponentSide = "away";

      const opponent: OwnerTeam = getOpponentOwnerTeam(
        ownerTeams,
        (matchup[opponentSide] && matchup[opponentSide].teamId) || 0,
        season.seasonId
      );

      let result: string;

      if (matchup.winner === teamSide.toUpperCase()) {
        result = "W";
      } else if (matchup.winner === "TIE") {
        result = "T";
      } else {
        result = "L";
      }

      const score: MatchupResult = {
        isChampionship: matchup.matchupPeriodId === season.status.currentMatchupPeriod, //todo this only works if it is single week playoff matches
        isPlayoffGame: matchup.playoffTierType === "WINNERS_BRACKET",
        points: matchup[teamSide].pointsByScoringPeriod[week],
        pointsAgainst: matchup[opponentSide] && matchup[opponentSide].pointsByScoringPeriod[week],
        opponent: opponent,
        result: result,
        week: parseInt(week),
        year: season.seasonId,
      };
      matchupScores.push(score);
    }

    return matchupScores;
  }

  function setHistoricalData(score: MatchupResult, ownerTeam: OwnerTeam, seasonId: number) {
    if (score.opponent.id !== 0) {
      if (ownerTeam.matchupHistory && !ownerTeam.matchupHistory.some(history => history.opponent.id === score.opponent.id)) {
        ownerTeam.matchupHistory.push({
          opponent: score.opponent,
          wins: 0,
          losses: 0,
          ties: 0,
          results: []
        })
      }

      const result = {
        ownerScore: score.points,
        opponentScore: score.pointsAgainst,
        year: seasonId,
        week: score.week
      };
      const currentMatchup = ownerTeam.matchupHistory && ownerTeam.matchupHistory.find(history => history.opponent.id === score.opponent.id);

      if (currentMatchup) {
        currentMatchup.results.push(result);

        if (result.ownerScore > result.opponentScore) {
          currentMatchup.wins++;
        } else if ( result.ownerScore < result.opponentScore) {
          currentMatchup.losses++;
        } else {
          currentMatchup.ties++;
        }
      }
    }

    if (ownerTeam.highScore && score.points > ownerTeam.highScore.points) {
      ownerTeam.highScore.points = score.points;
      ownerTeam.highScore.year = seasonId;
      ownerTeam.highScore.week = score.week;
      ownerTeam.highScore.opponent = score.opponent;
      ownerTeam.highScore.result = score.result;
    } 
    
    if (ownerTeam.lowScore && score.points < ownerTeam.lowScore.points) {
      ownerTeam.lowScore.points = score.points;
      ownerTeam.lowScore.year = seasonId;
      ownerTeam.lowScore.week = score.week;
      ownerTeam.lowScore.opponent = score.opponent;
      ownerTeam.lowScore.result = score.result;
    }

    if (score.isPlayoffGame) {
      if (ownerTeam.playoffYears && !ownerTeam.playoffYears.includes(seasonId)) {
        ownerTeam.playoffYears.push(seasonId);
      }

      if (score.result === "W") {
        if (!ownerTeam.playoffWins) ownerTeam.playoffWins = 0;
        ownerTeam.playoffWins++;

        if (score.isChampionship && ownerTeam.championshipYears && !ownerTeam.championshipYears.includes(seasonId)) {
          ownerTeam.championshipYears.push(seasonId);
        }
      }
    }
  }

  function setCurrentSeasonData(score: MatchupResult, ownerTeam: OwnerTeam) {
    if (ownerTeam.currentSeasonGames) {
      ownerTeam.currentSeasonGames.pointsFor += score.points;
      ownerTeam.currentSeasonGames.pointsAgainst += score.pointsAgainst;
      ownerTeam.currentSeasonGames.wins += score.result === "W" ? 1 : 0;
      ownerTeam.currentSeasonGames.losses += score.result === "L" ? 1 : 0;
      ownerTeam.currentSeasonGames.ties += score.result === 'T' ? 1 : 0;
      ownerTeam.currentSeasonGames.matchupCount++;
    }
  }

  function setRecentGameData(score: MatchupResult, ownerTeam: OwnerTeam) {
    if (ownerTeam.recentGames) {
      ownerTeam.recentGames.pointsFor += score.points;
      ownerTeam.recentGames.pointsAgainst += score.pointsAgainst;
      ownerTeam.recentGames.wins += score.result === "W" ? 1 : 0;
      ownerTeam.recentGames.losses += score.result === "L" ? 1 : 0;
      ownerTeam.recentGames.ties += score.result === 'T' ? 1 : 0;
      ownerTeam.recentGames.matchupCount++;
    }
  }

  function isRecentGame(score: MatchupResult, season: Season) {
    if (score.year !== currentYear || score.week < season.status.currentMatchupPeriod - 3 || score.week >= season.status.currentMatchupPeriod) {
      return false;
    }

    return true;
  }

  function setScoreDataPerTeam(
    ownerTeam: OwnerTeam,
    matchupScores: MatchupResult[],
    season: Season
  ): void {
    for (
      let scoresIndex = 0; scoresIndex < matchupScores.length; scoresIndex++) {
      const score = matchupScores[scoresIndex];

      setHistoricalData(score, ownerTeam, season.seasonId);

      if (score.year === currentYear) {
        setCurrentSeasonData(score, ownerTeam);

        if (isRecentGame(score, season)) {
          setRecentGameData(score, ownerTeam);
        }
      }
    }
  }

  function isIncompleteScoringPeriod(season: Season, matchup: Matchup) {
    return (
      season.seasonId === currentYear &&
      matchup.matchupPeriodId >= season.scoringPeriodId
    );
  }

  function isOwnerInMatchup(ownerTeam: OwnerTeam, matchup: Matchup, seasonId: number, teamSide: 'home' | 'away') {
    return ownerTeam.teamYears && ownerTeam.teamYears.some(
      (teamYear) =>
        teamYear.year === seasonId &&
        matchup[teamSide] &&
        teamYear.team === matchup[teamSide].teamId
    );
  }

  for (let i = 0; i < seasons.length; i++) {
    const season = seasons[i];
    for (let k = 0; k < season.members.length; k++) {
      const member: Owner = season.members[k];


      if (!ownerTeams.some((x) => x.owner && x.owner.id === member.id)) {
        if (ownerTeams.some(x => x.owner && x.owner.firstName === member.firstName && x.owner.lastName === member.lastName)) {

        }
        memberCount++;
        const ownerTeam: OwnerTeam = {
          id: memberCount,
          currentSeasonGames: {
            pointsFor: 0,
            pointsAgainst: 0,
            wins: 0,
            losses: 0,
            ties: 0,
            matchupCount: 0,
          },
          recentGames: {
            pointsFor: 0,
            pointsAgainst: 0,
            wins: 0,
            losses: 0,
            ties: 0,
            matchupCount: 0,
          },
          matchupHistory: [],
          owner: member,
          teams: [],
          teamYears: [],
          highScore: {
            points: 0,
          },
          lowScore: {
            points: 500,
          },
          championshipYears: [],
          playoffYears: [],
          playoffWins: 0,
          experience: 0,
        };
        ownerTeams.push(ownerTeam);
      }
    }
  }

  for (let i = 0; i < seasons.length; i++) {
    const season: Season = seasons[i];

    for (let j = 0; j < season.teams.length; j++) {
      const team: Team = season.teams[j];

      for (let i = 0; i < ownerTeams.length; i++) {
        const ownerTeam: OwnerTeam = ownerTeams[i];
        if (ownerTeam.owner.id === team.owners[0] && ownerTeam.teams && ownerTeam.teamYears) {
          ownerTeam.teams.push(team);
          ownerTeam.teamYears.push({ year: season.seasonId, team: team.id });
          if (season.seasonId !== currentYear) {
            if (!ownerTeam.experience) ownerTeam.experience = 0;
            ownerTeam.experience++;
          }
        }
      }
    }

    for (let matchupIndex = 0; matchupIndex < season.schedule.length; matchupIndex++) {
      const matchup: Matchup = season.schedule[matchupIndex];
      if (isIncompleteScoringPeriod(season, matchup)) {
        continue;
      }

      for (let i = 0; i < ownerTeams.length; i++) {
        const ownerTeam: OwnerTeam = ownerTeams[i];
        if (isOwnerInMatchup(ownerTeam, matchup, season.seasonId, "away")) {
          const matchupScores = getMatchupScores(matchup, "away", season);
          setScoreDataPerTeam(ownerTeam, matchupScores, season);
        } else if (isOwnerInMatchup(ownerTeam, matchup, season.seasonId, "home")) {
          const matchupScores = getMatchupScores(matchup, "home", season);
          setScoreDataPerTeam(ownerTeam, matchupScores, season);
        }
      }
    }
  }

  for (let i = 0; i < ownerTeams.length; i++) {
    const ownerTeam: OwnerTeam = ownerTeams[i];
    const team: Team = {
      id: 0,
      owner: {
        firstName: ownerTeam.owner.firstName.toString(),
        lastName: ownerTeam.owner.lastName.toString(),
        id: ownerTeam.owner.id,
      },
      owners: [],
      record: {
        overall: {
          wins: 0,
          losses: 0,
          ties: 0,
          pointsFor: 0,
          pointsAgainst: 0,
        },
      },
      records: ownerTeam,
    };

    if (ownerTeam.teams) {  
      for (let i = 0; i < ownerTeam.teams.length; i++) {
        const current: Team = ownerTeam.teams[i];
        if (team.record && current.record) {
          team.record.overall.wins += current.record.overall.wins;
          team.record.overall.losses += current.record.overall.losses;
          team.record.overall.ties += current.record.overall.ties;
          team.record.overall.pointsFor += current.record.overall.pointsFor;
          team.record.overall.pointsAgainst += current.record.overall.pointsAgainst;
        }
      }
    }

    teams.push(team);
  }

  for (let i = 0; i < teams.length; i++) {
    const team: Team = teams[i];

    if (team.record) {
      const totalGames = team.record.overall.wins + team.record.overall.losses + team.record.overall.ties;
      team.pointsAgainstAverage = (team.record.overall.pointsAgainst / totalGames).toFixed(2);
      team.pointsForAverage = (team.record.overall.pointsFor / totalGames).toFixed(2);
      team.winPercentage = (team.record.overall.wins / totalGames).toFixed(3)
      if (team.winPercentage && parseFloat(team.winPercentage) < 1.0) {
        team.winPercentage = team.winPercentage.substr(1);
      }
    }
  }

  return orderBy(teams, "winPercentage", "desc");
}

function useHistory() {
  const store = useStore();
  const teams = ref<Team[]>([]);
  const selectedTeam = ref<Team>();
  const router = useRouter();

  async function getLeagueHistory() {
    teams.value = consolidateSeasons(store.state.league);
  }

  watch(
    () => store.state.leagueId, 
    () => {
      teams.value = [];
      selectedTeam.value = undefined;
      getLeagueHistory();
    }
  )

  watch(
    () => store.state.league, 
    () => {
      teams.value = [];
      selectedTeam.value = undefined;
      getLeagueHistory();
    }
  )

  function showTeamDetail(team: Team) {
    selectedTeam.value = team;
  }

  getLeagueHistory();

  return { teams, router, showTeamDetail, selectedTeam };
}

export default {
  components: { OwnerCard },
  setup() {
    return useHistory();
  },
};
